/**
 * Which key opens which game on the picker.
 *
 * Nine games is where the obvious answer runs out. The picker opens a game on
 * the *first* digit pressed — deliberately, because "press 2, land on a
 * highlighted card, confirm it" is two steps where she asked for one — so a
 * two-digit shortcut is not merely unimplemented, it cannot exist on that
 * path. A tenth game would have drawn a `10` on its card and had the legend
 * announce "1 – 10", both naming a key nobody can press. An instruction on
 * screen that is not true is worse than no instruction, and this app has been
 * caught by exactly that before.
 *
 * So the tenth game onwards get a letter. One press each, still no confirming
 * step, and letters are free on this screen — the picker only ever wanted
 * digits and the arrows.
 *
 * All of it lives here rather than in the component because the interesting
 * cases are the ones the roster cannot reach yet: six games are registered and
 * the tenth is the one that breaks. A test that asks the real roster can only
 * ever check the easy half.
 */

/** Games one to nine keep the numbers she already knows. */
const NUMBERED = 9

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

/**
 * The key that opens the game in this position, or `null` for a game so far
 * down the roster that it has run out of both — thirty-six games in. It is
 * still reachable with the arrows, and drawing no badge is the honest way to
 * say it has no key of its own.
 */
export function shortcutFor(index: number): string | null {
  if (index < 0) return null
  if (index < NUMBERED) return String(index + 1)

  return LETTERS[index - NUMBERED] ?? null
}

/**
 * Which game a keypress opens, or `null` if that key opens none.
 *
 * `count` matters: pressing `7` with six games registered must do nothing
 * rather than reach for a card that is not there.
 */
export function gameAtKey(key: string, count: number): number | null {
  const index = indexForKey(key)

  return index !== null && index < count ? index : null
}

function indexForKey(key: string): number | null {
  if (/^[1-9]$/.test(key)) return Number(key) - 1

  // Zero is not a shortcut. It would have to mean the tenth game, and reading
  // "0" as "10" is the sort of thing that makes sense only to whoever wrote it.
  if (/^[a-zA-Z]$/.test(key)) {
    const at = LETTERS.indexOf(key.toUpperCase())

    return at === -1 ? null : NUMBERED + at
  }

  return null
}

/**
 * How to describe the shortcuts in the key legend, for a roster this long.
 *
 * Built from the same two rules as the badges, so the legend cannot come to
 * promise a key the cards do not draw. A range is written as first, dash, last
 * — the legend renders that as two keys with a dash between, and reads it
 * aloud as "one to nine".
 */
export function shortcutLegend(count: number): { keys: string[]; does: string }[] {
  if (count <= 0) return []

  const numbered = Math.min(count, NUMBERED)
  const lettered = Math.max(0, Math.min(count, NUMBERED + LETTERS.length) - NUMBERED)

  /*
   * "That one", not "that game".
   *
   * The last entry on the menu is the room rather than a game, and it shares
   * this numbering deliberately — so a legend that said "open that game" over
   * a range ending on "Play together" would be an instruction on screen that
   * is not true, which this app has already shipped once and does not intend
   * to again.
   */
  const entries = [{ keys: keysFor('1', shortcutFor(numbered - 1)), does: 'Open that one' }]

  if (lettered > 0) {
    entries.push({
      keys: keysFor('A', shortcutFor(NUMBERED + lettered - 1)),
      // A different sentence, because the legend uses this as its React key.
      // Two entries sharing one do both get drawn — checked, rather than
      // assumed, after this comment claimed otherwise — but they become a
      // reconciliation hazard the moment the list changes, which is a worse
      // bug than one that shows up straight away.
      does: 'Open one of the rest',
    })
  }

  return entries
}

/** One key on its own when the range has one end, first–last when it has two. */
function keysFor(first: string, last: string | null): string[] {
  return last === null || last === first ? [first] : [first, '–', last]
}
